import { Box, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import PhoneFrameComponent from "../../../onboarding/PhoneFrame";
import { Button } from "@mantine/core";
import ArrowRightAltTwoToneIcon from "@mui/icons-material/ArrowRightAltTwoTone";
import dataUploadIcon from "../../../../assets/image/svg/data-upload.svg";
import configIcon from "../../../../assets/image/svg/config.svg";
import "./productionMovementScreen.css";
import { useState } from "react";
import ProductionMovementConfirmationPopup from "../popups/productionMovementConfirmationPopup";
import { useNavigate } from "react-router-dom";
import { updateOnboardingStatus } from "../../../../services/userOnboardService/userOnboardService";

interface UpdateOptionsProps {
    icon: string;
    label: string;
    description: string;
    color: string;
    handleEditClick: () => void;
}

const UpdateOptions = (props: UpdateOptionsProps) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <Stack
            direction={"row"}
            justifyContent="space-between"
            sx={{
                background: `linear-gradient(to right, ${props.color}0F, #FFFFFF)`,
                padding: "15px",
                borderRadius: "5px",
                marginTop: "20px",
            }}
        >
            <Box>
                <Stack direction={"row"} alignItems={"center"}>
                    <div
                        style={{
                            borderRadius: "10px",
                            backgroundColor: "white",
                            marginRight: "10px",
                            padding: "5px",
                        }}
                    >
                        <img
                            src={props.icon}
                            alt="icon"
                            height="25px"
                            width="25px"
                            style={{ margin: 0, padding: 0 }}
                        />
                    </div>
                    <Typography
                        color={props.color}
                        fontSize={17}
                        fontWeight={600}
                    >
                        {props.label}
                    </Typography>
                </Stack>
                <Typography color={"gray"} fontSize={12} marginTop="10px">
                    {props.description}
                </Typography>
            </Box>
            <Button
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onClick={props.handleEditClick}
                sx={{
                    fontSize: "12px",
                    backgroundColor: "white",
                    borderColor: "#17C3B2",
                    borderRadius: "15px",
                    color: "#17C3B2",
                    minHeight: "28px",
                    height: "28px",
                    "&:hover": {
                        backgroundColor: "#17C3B2",
                        color: "white",
                        borderColor: "#17C3B2"
                    }
                }}
            >
                <svg
                    className="update-option-svg"
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 20.407 20.065"
                    style={{
                        fill: isHovered ? "white" : "#17C3B2",
                        stroke: isHovered ? "white" : "#17C3B2",
                        strokeWidth: "0.1px",
                        marginRight: "2px",
                    }}
                >
                    <g
                        id="Group_79"
                        data-name="Group 79"
                        transform="translate(0 -4.296)"
                    >
                        <g
                            id="Group_76"
                            data-name="Group 76"
                            transform="translate(0 5.525)"
                        >
                            <g id="Group_75" data-name="Group 75">
                                <path
                                    id="Path_189"
                                    data-name="Path 189"
                                    d="M15.861,46.894a.99.99,0,0,0-.991.991V51a.992.992,0,0,1-.991.991H2.974A.992.992,0,0,1,1.983,51V38.112a.992.992,0,0,1,.991-.991h5.76a.991.991,0,0,0,0-1.983H2.974A2.978,2.978,0,0,0,0,38.112V51a2.978,2.978,0,0,0,2.974,2.974h10.9A2.977,2.977,0,0,0,16.853,51V47.886A.99.99,0,0,0,15.861,46.894Z"
                                    transform="translate(0 -35.138)"
                                ></path>
                            </g>
                        </g>
                        <g
                            id="Group_78"
                            data-name="Group 78"
                            transform="translate(5.71 4.296)"
                        >
                            <g
                                id="Group_77"
                                data-name="Group 77"
                                transform="translate(0 0)"
                            >
                                <path
                                    id="Path_190"
                                    data-name="Path 190"
                                    d="M156.853,5.4A3.816,3.816,0,0,0,151.5,5.39L144.784,12.1a5.174,5.174,0,0,0-1.519,3.674V18a.99.99,0,0,0,.991.991h2.221a5.174,5.174,0,0,0,3.675-1.52l6.7-6.7A3.8,3.8,0,0,0,156.853,5.4Zm-8.1,10.666a3.2,3.2,0,0,1-2.274.94h-1.229V15.775a3.2,3.2,0,0,1,.939-2.273l4.424-4.424,2.564,2.564Zm6.7-6.7-.875.875-2.564-2.564.875-.875a1.813,1.813,0,0,1,2.564,2.564Z"
                                    transform="translate(-143.265 -4.296)"
                                ></path>
                            </g>
                        </g>
                    </g>
                </svg>
                Edit
            </Button>
        </Stack>
    );
};

const ProductionMovementScreen = (props: {
    handleStepIconClick: (stepNumber: number) => void;
}) => {
    const theme = useTheme();
    const [open, setOpen] = useState(false);
    const largeScreen = useMediaQuery(theme.breakpoints.up("xl"));
    const navigate = useNavigate();

    return (
        <>
            <Stack
                direction={{ xs: "column", sm: "row" }}
                width={"100%"}
                bgcolor={"white"}
                height={"95%"}
                spacing={4}
                marginTop="25px"
            >
                <Stack
                    width={"60%"}
                    padding={"20px"}
                    paddingTop="5px"
                    marginY="0"
                    position={"relative"}
                    spacing={{ xl: 2 }}
                >
                    <Box paddingY={"10px"} marginY={{ sm: 2, xl: "10%" }}>
                        <Typography
                            variant="h3"
                            color={"#17C3B2"}
                            fontSize={{ sm: "3rem", xl: "7rem" }}
                            fontWeight={700}
                            marginTop={2}
                        >
                            You're All Set!
                        </Typography>
                        <Typography
                            paddingY="10px"
                            borderRadius={2}
                            marginY="5px"
                            color={"black"}
                            fontWeight={400}
                            fontSize={{ sm: "20px", xl: "20px" }}
                        >
                            Your app is
                            <span style={{ fontWeight: 600, color: "#227C9D" }}>
                                &nbsp;Ready for the Demo Environment
                            </span>
                        </Typography>
                    </Box>
                    <Stack
                        padding={"20px"}
                        position={"relative"}
                        spacing={{ xl: 2 }}
                        sx={{
                            height: "100%",
                            boxShadow: 3,
                            borderRadius: '8px',
                            padding: '16px',
                        }}
                    >
                        <Box>
                            <Typography>
                                You can edit the configuration and data, if
                                required
                            </Typography>
                        </Box>

                        <UpdateOptions
                            icon={configIcon}
                            label="Edit Config Setup"
                            color="#F66126"
                            description="Change or add from a range of standard and advanced configurations, and create a tailored app that aligns with your specific business needs."
                            handleEditClick={() => props.handleStepIconClick(2)}
                        />
                        <UpdateOptions
                            icon={dataUploadIcon}
                            label="Edit Data Upload"
                            color="#0076C4"
                            description="Edit, upload or check previous data, and create a tailored app that aligns with your specific business needs."
                            handleEditClick={() => props.handleStepIconClick(3)}
                        />

                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            marginTop="auto"
                        >
                            <Typography
                                sx={{
                                    flex: 1,
                                    fontSize: 13
                                }}
                            >
                                Move your app to production so it's available
                                for users to access
                            </Typography>
                            <Stack
                                sx={{
                                    flex: 1
                                }}
                                direction="row"
                                justifyContent="right"
                                alignItems="center"
                                marginTop="auto"
                            >
                                <Button
                                    onClick={async () => {
                                        await updateOnboardingStatus(false);
                                        navigate("/dashboard");
                                    }}
                                    style={{
                                        height: largeScreen ? "5rem" : undefined,
                                        padding: largeScreen ? "20px" : 6,
                                    }}
                                    sx={{
                                        border: "2px solid #237C9D",
                                        background: "white",
                                        "&:hover": {
                                            background: "white",
                                        },
                                        fontSize: largeScreen ? "40px" : undefined,
                                        color: "#237C9D"
                                    }}
                                >
                                    Go to Portal
                                </Button>
                                <Typography style={{
                                    marginLeft: "15px",
                                    marginRight: "15px"
                                }}>
                                    Or
                                </Typography>
                                <Button
                                    onClick={() => setOpen(true)}
                                    rightIcon={
                                        <ArrowRightAltTwoToneIcon
                                            sx={{ fontSize: { xl: "50px" } }}
                                        />
                                    }
                                    style={{
                                        height: largeScreen ? "5rem" : undefined,
                                        padding: largeScreen ? "20px" : 6,
                                    }}
                                    sx={{
                                        background: "#237C9D",
                                        "&:hover": {
                                            background: "#237C9D",
                                        },
                                        fontSize: largeScreen ? "40px" : undefined,
                                    }}
                                >
                                    Move to Production
                                </Button>
                            </Stack>
                        </Stack>
                    </Stack>
                </Stack>

                {/* right  */}
                <Box
                    width={"40%"}
                    height={"100%"}
                    // bgcolor={"#EAFFFD"}
                    // border={1}
                    // borderRadius={2}
                    // borderColor={"#07C8B4"}
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <PhoneFrameComponent heightValue="90%" widthValue="55%" />
                </Box>
            </Stack>
            <ProductionMovementConfirmationPopup open={open} setOpen={setOpen} />
        </>
    );
};

export default ProductionMovementScreen;
